//* Copie e cole este template para criar uma nova fase
<template>
  <div v-if="!!phaseData">
    <v-sheet v-if="loading || error" class="d-flex flex-column align-center justify-center row-gap-2" height="50vh">
      <template v-if="error">
        <h3>Um erro ocorreu ao tentar carregar os dados da tela!</h3>
        <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
          >tentar novamente
          <v-icon class="ml-2" large>mdi-refresh</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <h3>Carregando dados...</h3>
        <v-progress-circular indeterminate />
      </template>
    </v-sheet>
    <div v-else>
      <radio-options
        label="O paciente tem diagnóstico de artrite psoriásica?"
        :options="diagnosisOptions"
        v-model="phaseData['diagnostico_artrite_psoriatica'].value"
        row
        hide-details
        clearable
        :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
        :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
      />
      <field-label v-if="showPestCaspar" label="Preencha ao menos um dos dois parâmetros:" class="mt-6">
        <v-expansion-panels v-if="showPestCaspar" flat class="mt-2" multiple v-model="panel" active-class="active-border">
          <v-expansion-panel class="mb-4 panel-border">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-medium"> PEST</v-expansion-panel-header>
            <v-expansion-panel-content>
              <radio-options
                v-for="option in pestOptions"
                :key="option.key"
                clearable
                :label="option.name"
                v-model="phaseData.pest[option.key].value"
                row
                :options="defaultYesNoOptions"
                :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
                :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
          <v-expansion-panel class="panel-border">
            <v-expansion-panel-header class="text-subtitle-1 font-weight-medium"> CASPAR</v-expansion-panel-header>
            <v-expansion-panel-content>
              <p class="caspar-subtitle">Exame complementar que aumentará ainda mais a precisão do diagnóstico.</p>
              <div class="mt-4">
                <radio-options
                  v-for="option in casparOptions"
                  v-show="option.key !== 'caspar_condicao_psoriase' || phaseData.caspar['caspar_psoriase'].value"
                  :key="option.key"
                  clearable
                  :label="option.name"
                  v-model="phaseData.caspar[option.key].value"
                  row
                  :options="option.options || defaultYesNoOptions"
                  :required="pharmaceuticalDomainConfig.SUMMARIZEDFLOW"
                  :rules="pharmaceuticalDomainConfig.SUMMARIZEDFLOW ? [$validation.radioRequired()] : []"
                  @input="checkCaspar(option.key, $event)"
                />
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </field-label>
      <attendance-step-note class="py-2 mt-8" />
    </div>
  </div>
</template>
<script>
import BasePhase from '../../../general/phase/BasePhase.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import FieldLabel from '@/components/FieldLabel.vue'

import { createNamespacedHelpers } from 'vuex'
import AttendanceStepNote from '@/components/AttendanceForm/psoriasis/phase/v2/AttendanceStepNote.vue'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

export default {
  components: { AttendanceStepNote, RadioOptions, FieldLabel },
  extends: BasePhase,
  computed: {
    ...attendanceGettersMap(['attendance', 'attendanceDiseaseId']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    showPestCaspar() {
      if (!this.phaseData) {
        return false
      }

      return this.phaseData['diagnostico_artrite_psoriatica']?.value === 'NAO_SEI'
    },
    defaultYesNoOptions() {
      return [
        { text: 'Sim', value: true },
        { text: 'Não', value: false },
      ]
    },
    diagnosisOptions() {
      return [
        { text: 'Sim', value: 'SIM' },
        { text: 'Não sei', value: 'NAO_SEI' },
        { text: 'Não', value: 'NAO' },
      ]
    },
    casparOptions() {
      return [
        { key: 'caspar_psoriase', name: 'Psoríase' },
        {
          key: 'caspar_condicao_psoriase',
          name: 'Indique a condição da psoríase',
          options: [
            { text: 'Atual', value: 'ATUAL' },
            { text: 'Prévia', value: 'PREVIA' },
            { text: 'História familiar', value: 'HISTORIA_FAMILIAR' },
          ],
        },
        { key: 'caspar_distrofia_ungueal', name: 'Distrofia ungueal' },
        { key: 'caspar_fator_reumatoide_negativo', name: 'Fator reumatoide negativo' },
        { key: 'caspar_dactilite', name: 'Histórico ou presença de dactilite' },
        { key: 'caspar_evidencia_neoformacao_ossea', name: 'Rx com evidência de neoformação óssea' },
      ]
    },
    pestOptions() {
      return [
        { key: 'pest_articulacao_edemaciada', name: 'Já teve alguma articulação edemaciada?' },
        { key: 'pest_artrite_previa_diagnosticada', name: 'Algum médico já disse que tem artrite?' },
        { key: 'pest_orificios_unhas', name: 'Tem orifícios, furinhos nas unhas da mãos ou pés?' },
        { key: 'pest_dor_calcanhar', name: 'Já teve dor no calcanhar?' },
        {
          key: 'pest_dedo_salsicha',
          name: 'Já teve um dedo edemacionado ou dolorido sem motivo aparente? (Dedo em salsicha)',
        },
      ]
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      panel: [],
    }
  },
  mounted() {
    if (this.attendance.status === 'F') {
      this.panel = [0, 1]
    }
  },
  methods: {
    buildEmptyCaspar() {
      return this.casparOptions.reduce((acc, option) => ({ ...acc, [option.key]: { value: null } }), {})
    },
    buildEmptyPest() {
      return this.pestOptions.reduce((acc, option) => ({ ...acc, [option.key]: { value: null } }), {})
    },
    checkCaspar(key, value) {
      if (key === 'caspar_psoriase') {
        this.phaseData.caspar['caspar_condicao_psoriase'] = { value: null }
      }
    },
    getDefaultData() {
      return {
        caspar: { ...this.buildEmptyCaspar() },
        pest: { ...this.buildEmptyPest() },
        diagnostico_artrite_psoriatica: { value: null },
        notes: null,
      }
    },
  },
  watch: {
    showPestCaspar(newVal) {
      if (!newVal) {
        this.phaseData.pest = { ...this.buildEmptyPest() }
        this.phaseData.caspar = { ...this.buildEmptyCaspar() }
      }
    },
  },
}
</script>

<style scoped>
.panel-border {
  border: 1px solid #e6e6e6;
  border-radius: 4px;
}

.caspar-subtitle {
  letter-spacing: 0.25px;
  color: #2b2b2b;
}

.active-border {
  border: 2px solid #1976d2;
  transition: none !important;
}
</style>
